/**
 * The Free plan's vocabulary, as plain data and pure functions.
 *
 * Limits live on the server (docs/plg-free-plan.md): the UI only explains
 * them. Everything here is about reading what the API said — a limit, a usage
 * pair, a 403 quota payload — defensively, because an API older than the free
 * plan simply leaves the new fields out, and "absent" must read as "no limit".
 */

/** Limit keys the plan model already had. */
export type LegacyLimitKey =
  | 'maxProducts'
  | 'maxImportProducts'
  | 'maxSpaces'
  | 'maxVisualizationsPerMonth'
  | 'maxLeads'
  | 'maxEmailsPerMonth'
  | 'maxAutomationRules'
  | 'maxSocialAccounts'
  | 'maxScheduledPostsPerMonth'
  | 'maxSyncFlowsPerDay'
  | 'maxSeats'
  | 'monthlyCredits';

/** Limit keys the free plan added. Missing on any plan = -1 (unlimited). */
export type NewLimitKey =
  | 'maxRoomAnalysesPerMonth'
  | 'maxShopperChatsPerMonth'
  | 'maxSeamlessPerMonth'
  | 'max3dModelsPerMonth'
  | 'maxBrandReadsPerMonth'
  | 'maxMarketRefreshesPerMonth'
  | 'maxKeywordRows'
  | 'maxCompetitorRows'
  | 'maxGapRows'
  | 'maxConversationsVisible'
  | 'analyticsHistoryDays';

export type PlanLimitKey = LegacyLimitKey | NewLimitKey;

/** Every key with a label under FREE_PLAN.LIMITS, in the order a comparison reads them. */
export const PLAN_LIMIT_KEYS: PlanLimitKey[] = [
  'maxProducts',
  'maxImportProducts',
  'maxSpaces',
  'maxVisualizationsPerMonth',
  'maxRoomAnalysesPerMonth',
  'maxLeads',
  'maxEmailsPerMonth',
  'maxAutomationRules',
  'maxSocialAccounts',
  'maxScheduledPostsPerMonth',
  'maxSyncFlowsPerDay',
  'maxSeats',
  'monthlyCredits',
  'maxShopperChatsPerMonth',
  'maxSeamlessPerMonth',
  'max3dModelsPerMonth',
  'maxBrandReadsPerMonth',
  'maxMarketRefreshesPerMonth',
  'maxKeywordRows',
  'maxCompetitorRows',
  'maxGapRows',
  'maxConversationsVisible',
  'analyticsHistoryDays',
];

/** The keys the free plan introduced — the rows the upgrade page compares under "Limits and history". */
export const NEW_LIMIT_KEYS: NewLimitKey[] = [
  'maxRoomAnalysesPerMonth',
  'maxShopperChatsPerMonth',
  'maxSeamlessPerMonth',
  'max3dModelsPerMonth',
  'maxBrandReadsPerMonth',
  'maxMarketRefreshesPerMonth',
  'maxKeywordRows',
  'maxCompetitorRows',
  'maxGapRows',
  'maxConversationsVisible',
  'analyticsHistoryDays',
];

/** The meters the plan chip lists: the ones a Free vendor actually runs into. */
export const CHIP_METER_KEYS: PlanLimitKey[] = [
  'maxProducts',
  'maxVisualizationsPerMonth',
  'maxRoomAnalysesPerMonth',
  'maxShopperChatsPerMonth',
  'maxSeamlessPerMonth',
  'max3dModelsPerMonth',
];

const KNOWN = new Set<string>(PLAN_LIMIT_KEYS);
export const isKnownLimitKey = (key: string | null | undefined): key is PlanLimitKey => !!key && KNOWN.has(key);

/** A limit as a number: absent, null or garbage reads as -1 (unlimited). */
export const readLimit = (value: unknown): number => {
  const n = typeof value === 'number' ? value : typeof value === 'string' && value.trim() !== '' ? Number(value) : NaN;
  return Number.isFinite(n) ? n : -1;
};

export const isUnlimited = (limit: number | null | undefined) => limit === undefined || limit === null || limit < 0;

/** At or above this share of the limit, a meter turns amber. */
export const NEAR_RATIO = 0.8;

export type MeterState = 'unlimited' | 'ok' | 'near' | 'at';

/**
 * Where a usage pair stands. A limit of 0 means "none on this plan": there is
 * nothing left from the start, so it reads as at the limit.
 */
export function meterState(current: number | null | undefined, limit: number | null | undefined): MeterState {
  if (isUnlimited(limit)) return 'unlimited';
  const used = Math.max(0, Number(current) || 0);
  if (used >= (limit as number)) return 'at';
  if (used >= (limit as number) * NEAR_RATIO) return 'near';
  return 'ok';
}

/** Share of the limit used, 0–100, for a bar. */
export function meterPercent(current: number | null | undefined, limit: number | null | undefined): number {
  if (isUnlimited(limit)) return 0;
  if ((limit as number) === 0) return 100;
  return Math.min(100, Math.max(0, ((Number(current) || 0) / (limit as number)) * 100));
}

/** A usage pair as `GET plans/my-plan` reports it. */
export interface UsagePair {
  current: number;
  limit: number;
}

/** Read one usage entry defensively; null when the API did not report the key. */
export function readUsage(usage: unknown, key: string): UsagePair | null {
  const raw = usage && typeof usage === 'object' ? (usage as Record<string, any>)[key] : undefined;
  if (!raw || typeof raw !== 'object') return null;
  return { current: Math.max(0, Number(raw.current) || 0), limit: readLimit(raw.limit) };
}

/** The 403 quota shape from VendorQuotaGuard / UsageMeterService. */
export interface PlanLimitPayload {
  quotaType: string;
  message?: string;
  current?: number;
  limit?: number;
  currentPlan?: string;
  planName?: string;
  /** False when upgrading would not help (e.g. `activation`: finish signing up first). */
  upgrade?: boolean;
}

/**
 * The quota payload of a thrown request error, or null when it is not one.
 * Accepts the normalised error (`err.body`) or the body itself.
 */
export function readPlanError(error: unknown): PlanLimitPayload | null {
  if (!error || typeof error !== 'object') return null;
  const e = error as Record<string, any>;
  const body = e.body && typeof e.body === 'object' ? e.body : e;
  if (!body || typeof body.quotaType !== 'string' || !body.quotaType) return null;
  const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : undefined);
  return {
    quotaType: body.quotaType,
    message: typeof body.message === 'string' ? body.message : undefined,
    current: num(body.current),
    limit: num(body.limit),
    currentPlan: typeof body.currentPlan === 'string' ? body.currentPlan : undefined,
    planName: typeof body.planName === 'string' ? body.planName : undefined,
    upgrade: typeof body.upgrade === 'boolean' ? body.upgrade : undefined,
  };
}

/** Where every unlock prompt leads: the upgrade page, told why. */
export const UPGRADE_PATH = '/home/billing/upgrade';

export const upgradeHref = (reason?: string | null) =>
  reason ? `${UPGRADE_PATH}?reason=${encodeURIComponent(reason)}` : UPGRADE_PATH;

/** A plan counts as the free tier by its slug or its pricing type. */
export const isFreePlan = (plan: { slug?: string; pricingType?: string } | null | undefined) =>
  !!plan && (plan.slug === 'free' || plan.pricingType === 'free');
